import type { Variants } from "motion/react";
import { duration, easing } from "@/lib/animation/tokens";

/**
 * Mobile navigation animations.
 *
 * Five named variants cooperate:
 *  - `hamburgerLine*` for the three burger spans morphing into an X.
 *  - `panel` for the slide-in pop-out container.
 *  - `linkList` for the cascading stagger.
 *  - `linkItem` for each individual link's fade + slide.
 *
 * On close everything plays in reverse: links cascade out *before* the
 * panel itself fades, so the user sees the items pull away rather than
 * a flat fade.
 */

/* ---------------------------- Hamburger ---------------------------- */

/** Top line: at rest sits at y=-6; when open rotates 45° to centre. */
export const hamburgerTop: Variants = {
  closed: { rotate: 0, y: -6, transition: { duration: duration.sm, ease: easing.out } },
  open: { rotate: 45, y: 0, transition: { duration: duration.sm, ease: easing.out } },
};

/** Middle line: fades and collapses to zero when open. */
export const hamburgerMiddle: Variants = {
  closed: { opacity: 1, scaleX: 1, transition: { duration: duration.xs, ease: easing.out } },
  open: { opacity: 0, scaleX: 0, transition: { duration: duration.xs, ease: easing.out } },
};

/** Bottom line: mirror of top. */
export const hamburgerBottom: Variants = {
  closed: { rotate: 0, y: 6, transition: { duration: duration.sm, ease: easing.out } },
  open: { rotate: -45, y: 0, transition: { duration: duration.sm, ease: easing.out } },
};

/* ------------------------------ Panel ------------------------------ */

/**
 * Pop-out panel — slides down and fades in. `pointerEvents` toggled
 * inline so the closed panel doesn't catch taps once it's invisible.
 */
export const panelVariants: Variants = {
  closed: {
    opacity: 0,
    y: -20,
    pointerEvents: "none",
    transition: { duration: duration.sm, ease: easing.out },
  },
  open: {
    opacity: 1,
    y: 0,
    pointerEvents: "auto",
    transition: { duration: duration.md, ease: easing.outExpo },
  },
};

/* ------------------------ Link list + items ------------------------ */

/**
 * Container variants — drives the stagger. On open, children cascade
 * top-down with a small delay after the panel begins. On close, the
 * stagger plays in reverse so items fly off in the *opposite* order
 * (bottom-up), then the panel fades.
 */
export const linkListVariants: Variants = {
  closed: {
    transition: {
      staggerChildren: 0.04,
      staggerDirection: -1,
    },
  },
  open: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.08,
    },
  },
};

/** Each link slides in from the left with a fade. */
export const linkItemVariants: Variants = {
  closed: {
    opacity: 0,
    x: -16,
    transition: { duration: duration.sm, ease: easing.out },
  },
  open: {
    opacity: 1,
    x: 0,
    transition: { duration: duration.md, ease: easing.out },
  },
};
